"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GrabTheSchedule() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    number: "1200",
    label: "Watts of PA Power"
  }, {
    number: "24/7",
    label: "Studio Access"
  }, {
    number: "MMXXIV",
    label: "Est. Garage Room"
  }];
  return <section data-section-id="2182" id="grab-the-schedule" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={textRef} data-index={0} className="lg:col-span-7 transition-all duration-[600ms] ease-out" style={{
          opacity: textVisible ? 1 : 0,
          transform: textVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            <p className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] mb-8 text-primary">
              // THE_MANDATE
            </p>

            <h2 className="font-default font-extrabold leading-[0.9] mb-10 text-4xl md:text-5xl lg:text-6xl xl:text-[7rem] uppercase tracking-tighter text-foreground">
              Noise is not
              <br />
              negotiable. It is
              <br />
              <em className="italic font-extrabold text-primary not-italic">
                required.
              </em>
            </h2>

            <p className="font-default text-base md:text-lg leading-relaxed max-w-[480px] mb-10 text-muted-foreground">
              The Garage Room isn't just a rehearsal space; it's a high-gain sanctuary for the local scene. From tube-driven stacks to live tracking rigs, we provide the grit and the gear. Plug in, crank the master, and leave the neighbors behind.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 sm:items-center">
              <Button asChild size="lg" className="font-default font-bold text-base px-8 h-12 rounded-md bg-primary text-primary-foreground shadow-md hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_hsl(45_60%_30%)] transition-all">
                <Link to="/BookPage">Grab the Schedule</Link>
              </Button>

              <Link to="/TheGearPage" className="inline-flex items-center gap-2 font-default font-semibold text-base group text-primary">
                <span className="border-b-2 pb-1 transition-colors border-primary">
                  View Backline Specs
                </span>
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 relative">
            <div ref={imageRef} data-index={1} className="relative transition-all duration-[600ms] ease-out" style={{
            opacity: imageVisible ? 1 : 0,
            transform: imageVisible ? "translateY(0)" : "translateY(20px)"
          }}>
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border-2 border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_live.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_live.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_live.png-wvc-sizes" alt="A local band tracking live in a gritty studio with warm mustard lighting" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-125 hover:grayscale-0 transition-all duration-500" />
                <div aria-hidden="true" className="absolute inset-0 rounded-md pointer-events-none border border-primary/30" />
              </div>

              {}
              <Card ref={cardRef} className="absolute -bottom-10 -left-4 sm:-left-10 w-[78%] sm:w-[280px] p-7 rounded-md shadow-2xl transition-all duration-[600ms] ease-out bg-card border-2 border-border" style={{
              transitionDelay: "200ms",
              opacity: cardVisible ? 1 : 0,
              transform: cardVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                <div className="flex flex-col gap-5">
                  {stats.map((stat, i) => <div key={i} data-index={i} className={i < stats.length - 1 ? "pb-5 border-b border-border" : ""}>
                      <div className="font-default font-bold text-4xl leading-none mb-2 text-primary">
                        {stat.number}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {stat.label}
                      </div>
                    </div>)}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}